// ─────────────────────────────────────────────────────────────────────────────
// apiBase.ts — WHERE THE API IS, ANSWERED ONCE.
//
// ── THE FAULT THIS CLOSES, AND IT IS WHY THREE SURFACES READ "UNREAD" ──────
// Six of these clients each carried their own copy of this:
//
//     NODE_ENV === 'production' ? 'https://herexy.com/api'
//                               : 'http://localhost:5000/api'
//
// BOTH BRANCHES ARE WRONG AWAY FROM ONE MACHINE, and the production one is
// wrong in the worst way: `npm run build` sets NODE_ENV=production, so a BUILT
// bundle points at herexy.com WHEREVER IT IS SERVED FROM — including the box
// the owner is sitting at. Every call then leaves for a different host carrying
// a Bearer the local Flask minted, and comes back 401 or does not come back at
// all. The dev branch has the same shape of bug one step quieter: `localhost`
// is hardcoded, so reaching the box by LAN IP resolves :5000 on the VIEWER'S
// machine.
//
// THE SYMPTOM WAS THREE SENTENCES AND ONE CAUSE. `/api/rexy/agent-loop` unread
// on Arthur, `/api/rexy/agent-transport` "answered with something this surface
// could not read", `/api/growth/runner` unread on Buck — while
// `run_buck.py --status` answered instantly off the same store. The data was
// never missing. The surface was asking a different host.
//
// ── THE HOUSE ALREADY HAD THE ANSWER AND THESE FILES DID NOT USE IT ────────
// `App.tsx:64`, `TradingPage.tsx:14`, `CodePage.tsx:10`, `ChatWindow.tsx:115`
// and `InputBox.tsx:315` all derive it from `window.location`, and the
// dashboard feeds use bare relative `/api/...` paths. This is that same idiom,
// in ONE place, so the seventh client cannot inherit a seventh copy of the
// wrong one. It is deliberately byte-identical to App.tsx's:
//
//   https page → same host, through nginx     (herexy.com, and any future host)
//   http  page → that host's :5000 directly   (Electron, LAN, the DGX)
//
// DO NOT REGRESS THE http BRANCH. App.tsx's own note says the direct-to-Flask
// path is the countersigned LAN path and that Electron on localhost depends on
// it. Using `window.location.hostname` rather than the literal `localhost` is
// what makes it work from another machine as well as on the box.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The API root for the page currently being served, with no trailing slash.
 *
 * A FUNCTION, NOT A CONSTANT, and that is not stylistic: a module-level constant
 * is evaluated at import time, which in a test environment is before jsdom's
 * location can be set, and in a service worker context there is no `window` at
 * all. Reading it per call costs nothing and cannot be captured too early.
 */
export function apiBase(): string {
  // No `window` — a worker, a node test, a prerender. The relative root is the
  // only honest answer: it resolves against whatever origin does have one, and
  // it is what the dashboard feeds already use.
  if (typeof window === 'undefined' || !window.location) return '/api';
  const loc = window.location;
  if (loc.protocol === 'https:') return `https://${loc.host}/api`;
  // `hostname`, never `host` — the page's port is the UI's (3000 in dev, 8080
  // under Electron); Flask is on 5000 regardless.
  return `${loc.protocol}//${loc.hostname}:5000/api`;
}

/** The Bearer the shell injects on login (`App.tsx` sets it at sign-in). Empty
 *  string when absent, so the header is always well-formed and the failure is a
 *  401 the caller can read rather than a malformed request. */
export function authToken(): string {
  return (typeof window !== 'undefined' && (window as any).__rexyToken) || '';
}
